import { action, observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';

import { Pair } from '../../../adl-gen/sys/types';
import { CompetencyBarsSlideout } from '../../../adl-gen/totalinteraction/futurefit/db';
import { withI18n, WithI18nProps } from '../../../internationalization/i18n-hoc';
import { i18next } from '../../../internationalization/i18next';
import { PlotKeyToggleState } from '../../../stores/plot-key-toggle-state';
import {
  QuizResultsHelper
} from '../../../stores/quiz-results-store';
import { compareByGetTwoProperties } from '../../../util/utils';
import { SVGBuilder } from '../svg-builder/svg-builder';
import * as bars from './common';

const styles = require('./competency-bars.css');

export interface CompetencyBarsProps extends WithI18nProps {
  competencyId: string;
  qr: QuizResultsHelper;
  plotKeyToggleState: PlotKeyToggleState;
}

/** Circular Progress Bar  */
@observer
export class CompetencyBars0 extends React.Component<CompetencyBarsProps> {
  private readonly size: number = 500;

  private readonly widthMargin = 10;

  private readonly xStart = this.widthMargin;
  private readonly xEnd = 100 - this.xStart;

  private readonly detailFont = '10px Open SansRegular';

  // Column width such that the 3 columns have equal width and sides have width of widthMargin and all adds up to 100.
  /// --- [ FF2-433 ranges on WorkRole ] -------------------------------------------------
  // Support > 3 levels
  // 1. Store range sequence and size as well as legend names in db against workRole, as array not object. For example
  //     o Standard: seq 0 -> "Skilled" and 34%; 1 -> "Advanced" and 33%; 2 -> "Master" and 33%
  //     o 4-way: 0 -> "Progressing" and 50%; 1 -> "Skilled" and 20%; 2 -> "Advanced" and 20%; 3 -> "Master" and 10%
  // 2. Instead of hardcoded columnWidth constant, calculate a parallel array of extents from
  //    factor = (100 - 2 * widthMargin) / 100 (cf. getWidthFromScore)
  //    extents[n] = range[n] * factor
  // 3. Set up x-offsets from extent1 .. extent(n-1) as
  //      xOffset = this.xStart;
  //      separators = extents.map(extent => {
  //        const separator = this.scale(xOffset + extent);
  //        xOffset = xOffset + extent;
  //        return separator;
  //      })
  // private readonly columnWidth = (100 - 2 * this.widthMargin) / 3;

  @observable visibleProgressTooltip: string | undefined;
  @observable visibleDescTooltip: string | undefined;
  @observable visiblePreviousTooltip: string | undefined;
  @observable visibleGroupTooltip: string | undefined;
  @observable visibleIndustryTooltip: string | undefined;

  @action
  hoverOnProgressBar = (id: string) => {
    this.visibleProgressTooltip = id;
  };

  @action
  hoverOnInfo = (id: string) => {
    this.visibleDescTooltip = id;
  };

  @action
  hoverOffProgressBar = () => {
    this.visibleProgressTooltip = undefined;
  };

  @action
  hoverOffInfo = () => {
    this.visibleDescTooltip = undefined;
  };

  @action
  hoverOnPrevious = (id: string) => {
    this.visiblePreviousTooltip = id;
  };

  @action
  hoverOffPrevious = () => {
    this.visiblePreviousTooltip = undefined;
  };

  @action
  hoverOnGroup = (id: string) => {
    this.visibleGroupTooltip = id;
  };

  @action
  hoverOnIndustry = (id: string) => {
    this.visibleIndustryTooltip = id;
  };

  @action
  hoverOffGroup = () => {
    this.visibleGroupTooltip = undefined;
  };

  @action
  hoverOffIndustry = () => {
    this.visibleIndustryTooltip = undefined;
  };

  /// SS-569 Reorder skill progress bars highest->lowest gap not alphabetically
  /// compareByGet function for sorting skills in render()
  /// As we want the biggest gap at the top, we want the
  /// smallest ratio, so that means returning actual/expected
  rawSkillGapRatio = (qr: QuizResultsHelper, skillId: string): number => {
    const actual = this.getScoreFromPlotKey({
      kind: 'skill',
      qr,
      plotkey: 'actual',
      skillId
    });
    const expected = this.getScoreFromPlotKey({
      kind: 'skill',
      qr,
      plotkey: 'expected',
      skillId
    });
    if (expected === 0 || actual === undefined || expected === undefined) {
      return 0;
    } else {
      return actual / expected;
    }
  };

  /// SS-569 ... but what if rawSkillGapRatio returns zero?
  /// We need a two step process...
  /// 1. Run comparison by number first
  /// 2. If this returns zero:
  /// 3. Then compare by name.
  skillOrder = (qr: QuizResultsHelper, skillId: string): Pair<number, string> => {
    const skillGapRatio = this.rawSkillGapRatio(qr, skillId);
    const skillName = this.props.t(qr.skill(skillId).name);
    return { v1: skillGapRatio, v2: skillName };
  };

  render() {
    const { qr, t } = this.props;

    const skillIds = qr.getSkillsByCompetency(this.props.competencyId)
      .filter(skillId => qr.scoreRatioOfExpected({ kind: 'skill', skillId }) !== undefined)
      .sort(compareByGetTwoProperties(text => this.skillOrder(qr, text)));
    // alpha sort by name only
    // .sort(compareByGet(text => t(qr.skill(text).name)));

    /// 4 = lineOffset/yOffset (see below)
    /// 8.5 = titleOffset
    /// 20 = not defined anywhere... (gap between bars - first one = for Competency bar)
    /// 20 * skillIds.length = number of skills

    const lineOffset = 4;
    const titleOffset = 8.5;
    const yGap = 20;

    // const displaySkills = qr.quiz.competencyBars === CompetencyBarsSlideout.all;
    const displayDescription = qr.quiz.competencyBars === CompetencyBarsSlideout.noSkills;

    // Do this here just to ensure we get the right dimY size when there are no skills displayed
    const competencyDesc = t(qr.competency(this.props.competencyId).definition);
    const descBoxHeight = this.calcDescriptionBoxHeight(
      bars.wrapLabel(competencyDesc, this.scale(20), this.detailFont).length
    );

    const dimY = displayDescription
      ? this.scale(lineOffset + titleOffset + yGap + descBoxHeight)
      : // Assume displaying skills
      this.scale(lineOffset + titleOffset + yGap + yGap * skillIds.length);

    const builder = new SVGBuilder(1, {
      posX: 0,
      posY: 0,
      dimX: this.size,
      dimY
    });

    const { ranges } = qr;

    const factor = (100 - 2 * this.widthMargin) / 100;
    const extents: number[] = ranges.map(r => (r.scoreRange.range.v2 - r.scoreRange.range.v1 + 1) * factor);
    // Remove the last extent
    extents.pop();

    // Range Markers. FF2-433: Allow for more than 3 and for different labels
    // Initialize
    let xOffset = this.xStart;

    const separators = [
      this.xStart,
      ...extents.map(extent => {
        const separator = xOffset + extent;
        xOffset = separator;
        return separator;
      }),
      this.xEnd
    ].map(this.scale);

    // // Apprentice/Journeyman/Master markers
    // const separators = [
    //   this.xStart,
    //   this.xStart + this.columnWidth,
    //   this.xStart + 2 * this.columnWidth,
    //   /// >3 levels
    //   // this.xStart + this.extent1,
    //   // this.xStart + this.extent2,
    //   this.xEnd
    // ].map(this.scale);

    let yOffset = lineOffset;

    for (const x of separators) {
      builder.addLine({
        x1: x,
        y1: this.scale(yOffset - 2),
        x2: x,
        y2: this.scale(yOffset),
        style: 'solid',
        stroke: 'light'
      });
      if (x !== this.scale(this.xStart) && x !== this.scale(this.xEnd)) {
        builder.addLine({
          x1: x,
          y1: this.scale(yOffset),
          x2: x,
          y2: dimY,
          style: 'dashed',
          stroke: 'faint'
        });
      }
    }
    builder.addLine({
      x1: this.scale(this.xStart),
      y1: this.scale(yOffset),
      x2: this.scale(this.xEnd),
      y2: this.scale(yOffset),
      style: 'solid',
      stroke: 'light'
    });

    // /// FF2-429 A/J/M => different names according to quiz
    // let barCategories: BarCategories | undefined = qr.getBarCategories(t);
    // if (barCategories === undefined) {
    //   barCategories = {
    //     apprentice: i18next.t("competency-bars.categories.apprentice", "Apprentice"),
    //     journeyman: i18next.t("competency-bars.categories.journeyman", "Journeyman"),
    //     master: i18next.t("competency-bars.categories.master", "Master")
    //   };
    // }

    // Range Markers. FF2-433: Allow for more than 3 and for different labels
    // Re-initialize
    xOffset = this.xStart;

    ranges.map(r => {
      builder.addText({
        x: this.scale(xOffset + ((r.scoreRange.range.v2 - r.scoreRange.range.v1 + 1) * factor) / 2),
        y: this.scale(yOffset - 2),
        text: t(r.scoreRange.label),
        anchor: 'center',
        style: 'detail'
      });
      xOffset = xOffset + (r.scoreRange.range.v2 - r.scoreRange.range.v1 + 1) * factor;
    });

    // builder.addText({
    //   x: this.scale(50 - this.getWidthFromScore(100 / 3)),
    // /// >3 levels
    //   // x: this.scale(xOffset + this.extent1 / 2),
    //   y: this.scale(yOffset - 2),
    //   text: barCategories.apprentice,
    //   anchor: "center",
    //   style: "detail"
    // });
    // // xOffset = this.xStart + this.extent1;

    // builder.addText({
    //   x: this.scale(50),
    //   // x: this.scale(xOffset + (this.extent2 - this.extent1) / 2),
    //   y: this.scale(yOffset - 2),
    //   text: barCategories.journeyman,
    //   anchor: "center",
    //   style: "detail"
    // });
    // // xOffset = this.xStart + this.extent2;

    // builder.addText({
    //   x: this.scale(50 + this.getWidthFromScore(100 / 3)),
    //   // x: this.scale(xOffset + (this.xEnd - xOffset) / 2),
    //   y: this.scale(yOffset - 2),
    //   text: barCategories.master,
    //   anchor: "center",
    //   style: "detail"
    // });

    yOffset += titleOffset;

    {
      /// FF2-427 Display the full name here
      // const title = t(qr.competency(this.props.competencyId).name);
      const title = t(qr.competency(this.props.competencyId).fullName);

      /// SS-514 Remove icon from Competency bars by setting these three to zero
      // const iconWidth = 3;
      // const iconYOffset = -0.3;
      // const margin = 1;
      const iconWidth = 0;
      const margin = 0;
      const iconYOffset = 0;
      const barHeight = 3;
      const tooltipYOffset = -1.2;

      this.renderBarCollection(builder, {
        qr,
        title,
        desc: competencyDesc,
        iconWidth,
        barHeight,
        margin,
        iconYOffset,
        tooltipYOffset,
        style: 'title',
        yOffset,
        id: this.props.competencyId
      });
    }

    /// skill bars
    if (qr.quiz.competencyBars === CompetencyBarsSlideout.all) {
      for (const skillId of skillIds) {
        yOffset += yGap;
        const title = t(qr.skill(skillId).name);
        const desc = t(qr.skill(skillId).definition);

        const iconWidth = 2.5;
        const barHeight = 2;
        const margin = 1.5;
        const iconYOffset = -0.2;
        const tooltipYOffset = -0.7;

        this.renderBarCollection(builder, {
          qr,
          title,
          desc,
          iconWidth,
          barHeight,
          margin,
          iconYOffset,
          tooltipYOffset,
          style: 'subtitle',
          yOffset,
          id: skillId
        });
      }
    }

    return <div className={styles.container}>{builder.build()}</div>;
  }

  renderBarCollection(
    builder: SVGBuilder,
    {
      qr,
      yOffset,
      id,
      title,
      desc,
      iconWidth,
      barHeight,
      margin,
      iconYOffset,
      tooltipYOffset,
      style
    }: bars.BarCollectionProps
  ) {
    const toolTipLines = bars.wrapLabel(desc, this.scale(20), this.detailFont);
    /// Desc not displayed unless no skills
    const descBoxLines = bars.wrapLabel(desc, this.scale(20), this.detailFont);

    /// SS-514 remove info icon from Competency bars, and set title x-coord back to xStart.
    /// Detect this by checking margin, iconWidth and iconYOffset.
    /// If all three are zero, no icon.
    if (iconWidth !== 0 && iconYOffset !== 0 && margin !== 0) {
      this.addTooltip(
        builder,
        toolTipLines,
        this.xStart + iconWidth / 2,
        yOffset + tooltipYOffset,
        this.visibleDescTooltip === id
      );
      this.addInfoIcon(builder, id, this.xStart + iconWidth / 2, yOffset + iconYOffset, iconWidth);
    }

    builder.addText({
      x: this.scale(this.xStart + iconWidth + margin),
      y: this.scale(yOffset),
      text: title,
      anchor: 'right',
      style
    });

    /// Grab this for the descriptionbox if required
    const lastYOffset = this.addProgressBar(
      builder,
      yOffset,
      qr,
      barHeight,
      id !== this.props.competencyId ? id : undefined
    );

    /// If any of these three are non-zero, assume it's a competency bar (see above).
    /// Add the description box, if we're not displaying skills.
    if (iconWidth === 0 || iconYOffset === 0 || margin === 0) {
      this.addDescriptionBox(
        builder,
        descBoxLines,
        this.xStart + iconWidth / 2,
        lastYOffset,
        qr.quiz.competencyBars === CompetencyBarsSlideout.noSkills
      );
    }
  }

  getWidthFromPlotKey(props: bars.ProgressBarProps): number | undefined {
    const score = this.getScoreFromPlotKey(props);
    if (score === undefined) {
      return undefined;
    } else {
      return this.getWidthFromScore(score);
    }
  }

  getScoreFromPlotKey(props: bars.ProgressBarProps): number | undefined {
    if (!props.qr.plotExists(props.plotkey)) {
      return undefined;
    }
    if (props.kind === 'competency') {
      return props.qr.score(props.plotkey, {
        kind: 'competency',
        competencyId: this.props.competencyId
      });
    } else {
      return props.qr.score(props.plotkey, {
        kind: 'skill',
        skillId: props.skillId
      });
    }
  }

  getWidthFromScore(score: number): number {
    return (score * (this.xEnd - this.xStart)) / 100;
  }

  /// When not displaying skills, only competency, drop a description box
  /// in the empty space under the competency bars
  addDescriptionBox(builder: SVGBuilder, lines: string[], x: number, y: number, isVisible: boolean) {
    // Get whole width
    const boxWidth = this.getWidthFromScore(100);
    // (Math.max(...lines.map(l => getTextWidth(l, "10px Open SansRegular"))) /
    //   this.size) *
    //   100 +
    // 5;
    const boxHeight = this.calcDescriptionBoxHeight(lines.length);
    builder.addTextBox({
      x: this.scale(x),
      y: this.scale(y),
      width: this.scale(boxWidth),
      height: this.scale(boxHeight),
      text: lines.join(' '),
      style: 'detail',
      rectStyle: 'white',
      isVisible
    });
  }

  calcDescriptionBoxHeight(numLines: number): number {
    const lineHeight = 2.5;
    const boxHeight = lineHeight * (numLines + 1);
    const offset = 0;
    return boxHeight - offset;
  }

  addTooltip(builder: SVGBuilder, lines: string[], x: number, y: number, isVisible: boolean) {
    const boxWidth = (Math.max(...lines.map(l => bars.getTextWidth(l, '8px Open SansRegular'))) / this.size) * 100 + 5;
    const lineHeight = 2.5;
    const boxHeight = lineHeight * lines.length + 2.5;
    const offset = 0.5;
    builder.addRect({
      x: this.scale(x - boxWidth / 2),
      y: this.scale(y - boxHeight - offset),
      width: this.scale(boxWidth),
      height: this.scale(boxHeight - offset),
      style: 'white',
      isVisible
    });
    let i = lines.length;
    for (const line of lines) {
      builder.addText({
        x: this.scale(x),
        y: this.scale(y - offset - i * lineHeight),
        style: 'detail',
        anchor: 'center',
        text: line,
        isVisible
      });
      i--;
    }
  }

  addProgressBar(
    builder: SVGBuilder,
    yOffset: number,
    qr: QuizResultsHelper,
    rectHeight: number,
    skillId?: string
  ): number {
    const { competencyId, plotKeyToggleState, t } = this.props;
    let localYOffset = yOffset;
    const backgroundWidth = this.getWidthFromScore(100);
    const expectedWidth = this.getWidthFromPlotKey(
      skillId === undefined
        ? { kind: 'competency', qr, plotkey: 'expected' }
        : { kind: 'skill', qr, plotkey: 'expected', skillId }
    );
    const actualWidth = this.getWidthFromPlotKey(
      skillId === undefined
        ? { kind: 'competency', qr, plotkey: 'actual' }
        : { kind: 'skill', qr, plotkey: 'actual', skillId }
    );
    const previousWidth = this.getWidthFromPlotKey(
      skillId === undefined
        ? { kind: 'competency', qr, plotkey: 'previous' }
        : { kind: 'skill', qr, plotkey: 'previous', skillId }
    );
    const groupWidth = this.getWidthFromPlotKey(
      skillId === undefined
        ? { kind: 'competency', qr, plotkey: 'group' }
        : { kind: 'skill', qr, plotkey: 'group', skillId }
    );
    const industryWidth = this.getWidthFromPlotKey(
      skillId === undefined
        ? { kind: 'competency', qr, plotkey: 'industry' }
        : { kind: 'skill', qr, plotkey: 'industry', skillId }
    );

    /// DEBUG values
    const previousScore = this.getScoreFromPlotKey(
      skillId === undefined
        ? { kind: 'competency', qr, plotkey: 'previous' }
        : { kind: 'skill', qr, plotkey: 'previous', skillId }
    );
    const groupScore = this.getScoreFromPlotKey(
      skillId === undefined
        ? { kind: 'competency', qr, plotkey: 'group' }
        : { kind: 'skill', qr, plotkey: 'group', skillId }
    );
    const industryScore = this.getScoreFromPlotKey(
      skillId === undefined
        ? { kind: 'competency', qr, plotkey: 'industry' }
        : { kind: 'skill', qr, plotkey: 'industry', skillId }
    );

    const barOffset = 5;
    localYOffset += barOffset;

    builder.addRect({
      x: this.scale(this.xStart),
      y: this.scale(localYOffset),
      style: 'white',
      width: this.scale(backgroundWidth),
      height: this.scale(rectHeight),
      onMouseEnter: skillId
        ? () => this.hoverOnProgressBar(skillId)
        : () => this.hoverOnProgressBar(competencyId),
      onMouseLeave: this.hoverOffProgressBar
    });

    if (expectedWidth !== undefined && plotKeyToggleState.isShown('expected')) {
      const desiredMarkerY1 = localYOffset - 1.5;
      builder.addRect({
        x: this.scale(this.xStart),
        y: this.scale(localYOffset),
        style: 'grey',
        width: this.scale(expectedWidth),
        height: this.scale(rectHeight),
        onMouseEnter: skillId
          ? () => this.hoverOnProgressBar(skillId)
          : () => this.hoverOnProgressBar(competencyId),
        onMouseLeave: this.hoverOffProgressBar
      });
      builder.addLine({
        x1: this.scale(expectedWidth + this.xStart),
        y1: this.scale(desiredMarkerY1),
        x2: this.scale(expectedWidth + this.xStart),
        y2: this.scale(localYOffset + rectHeight),
        style: 'solid'
      });
      builder.addCircle({
        x: this.scale(expectedWidth + this.xStart),
        y: this.scale(desiredMarkerY1),
        r: this.scale(0.7),
        style: 'filled'
      });
      builder.addText({
        x: this.scale(expectedWidth + this.xStart - 2),
        y: this.scale(desiredMarkerY1) + 1,
        /// --- [ Possible TODO change if legend is quiz-specific ] ---
        text: i18next.t('competency-bars.legend.desired', 'DESIRED'),
        style: 'marker',
        anchor: 'left'
      });
    }

    if (actualWidth !== undefined && plotKeyToggleState.isShown('actual')) {
      const scoreText =
        skillId === undefined
          ? qr.scoreRatioOfExpectedText({
            kind: 'competency',
            competencyId: competencyId
          })
          : qr.scoreRatioOfExpectedText({ kind: 'skill', skillId });
      // const lines = [
      //   i18next.t("competency-bars.tooltips.towards", { percent: scoreText, defaultValue: `${scoreText} towards`}),
      //   i18next.t("competency-bars.tooltips.desired", `desired`)
      // ];
      /// --- [ Note ] ---
      /// This makes use of the returnObjects option to return an array
      /// (the translation item towards-desired is an array, and the '{{percent}}'
      /// interpolated value is on different array elements depending on the language
      /// selected), so that the array of strings can still be used to manually
      /// break lines in the tooltip.
      /// Have to use the spread operator ... to ensure it compiles.

      const lines = [
        ...i18next.t('competency-bars.tooltips.towards-desired', { returnObjects: true, percent: scoreText })
      ];
      const progressBarCount = builder.incrementRectCount();
      builder.addRect({
        x: this.scale(this.xStart),
        y: this.scale(localYOffset),
        style: 'orange',
        width: this.scale(actualWidth),
        height: this.scale(rectHeight),
        onMouseEnter: skillId
          ? () => this.hoverOnProgressBar(skillId)
          : () => this.hoverOnProgressBar(competencyId),
        onMouseLeave: this.hoverOffProgressBar,
        id: progressBarCount
      });
      this.addTooltip(
        builder,
        lines,
        this.xStart + actualWidth,
        localYOffset,
        skillId ? skillId === this.visibleProgressTooltip : competencyId === this.visibleProgressTooltip
      );
    }

    const plotOffset = 2;
    localYOffset += rectHeight + plotOffset;
    if (previousWidth !== undefined && plotKeyToggleState.isShown('previous')) {
      this.renderPreviousBar(
        builder,
        previousWidth,
        localYOffset,
        plotOffset,
        /// FF2-426
        qr.isDebuggingReports() && previousScore
          ? `${Math.round(previousScore)}`
          : i18next.t('competency-bars.tooltips.previous', 'Previous Results'),
        skillId
      );
    }


    localYOffset += plotOffset;
    if (groupWidth !== undefined && plotKeyToggleState.isShown('group')) {
      const groupLabel = qr.getGroupLabel(t);
      this.renderGroupBar(
        builder,
        groupWidth,
        localYOffset,
        plotOffset,
        /// FF2-426
        qr.isDebuggingReports() && groupScore
          ? `${Math.round(groupScore)}`
          : groupLabel !== undefined
            ? groupLabel
            : i18next.t('competency-bars.tooltips.group', 'Group Average'),
        skillId
      );
    }

    localYOffset += plotOffset;
    if (industryWidth !== undefined && plotKeyToggleState.isShown('industry')) {
      const industryLabel = qr.getIndustryLabel(t);
      this.renderIndustryBar(
        builder,
        industryWidth,
        localYOffset,
        plotOffset,
        /// FF2-426
        qr.isDebuggingReports() && industryScore
          ? `${Math.round(industryScore)}`
          : industryLabel !== undefined
            ? industryLabel
            : i18next.t('competency-bars.tooltips.industry', 'Industry Average'),
        skillId
      );
    }

    /// Return the current yOffset plus the right amount of space to add
    return localYOffset + plotOffset;
  }

  addInfoIcon(builder: SVGBuilder, id: string, x: number, y: number, size: number) {
    const iconCount = builder.incrementCustomItemCount();
    builder.addCustomElement(
      bars.infoIcon({
        key: id,
        x: this.scale(x - size / 2),
        y: this.scale(y - size / 2),
        width: this.scale(size),
        height: this.scale(size),
        onMouseEnter: () => this.hoverOnInfo(id),
        onMouseLeave: () => this.hoverOffInfo(),
        iconCount
      })
    );
    return builder;
  }

  renderPreviousBar(
    builder: SVGBuilder,
    width: number,
    yOffset: number,
    plotOffset: number,
    tooltip: string,
    skillId?: string
  ) {
    builder.addLine({
      x1: this.scale(this.xStart),
      y1: this.scale(yOffset),
      x2: this.scale(width + this.xStart),
      y2: this.scale(yOffset),
      style: 'dashed',
      stroke: 'dark'
    });
    builder.addRect({
      x: this.scale(this.xStart),
      y: this.scale(yOffset - plotOffset / 2),
      width: this.scale(width + 0.7),
      height: this.scale(plotOffset),
      style: 'transparent',
      onMouseEnter: skillId ? () => this.hoverOnPrevious(skillId) : () => this.hoverOnPrevious(this.props.competencyId),
      onMouseLeave: this.hoverOffPrevious
    });
    this.addTooltip(
      builder,
      [tooltip],
      this.xStart + width,
      yOffset - plotOffset / 2,
      skillId ? skillId === this.visiblePreviousTooltip : this.props.competencyId === this.visiblePreviousTooltip
    );
  }

  renderGroupBar(
    builder: SVGBuilder,
    width: number,
    yOffset: number,
    plotOffset: number,
    tooltip: string,
    skillId?: string
  ) {
    builder.addLine({
      x1: this.scale(this.xStart),
      y1: this.scale(yOffset),
      x2: this.scale(width + this.xStart),
      y2: this.scale(yOffset),
      style: 'dotted'
    });
    builder.addCircle({
      x: this.scale(width + this.xStart),
      y: this.scale(yOffset),
      style: 'shaded',
      r: this.scale(0.7)
    });
    builder.addRect({
      x: this.scale(this.xStart),
      y: this.scale(yOffset - plotOffset / 2),
      width: this.scale(width + 0.7),
      height: this.scale(plotOffset),
      style: 'transparent',
      onMouseEnter: skillId ? () => this.hoverOnGroup(skillId) : () => this.hoverOnGroup(this.props.competencyId),
      onMouseLeave: this.hoverOffGroup
    });
    this.addTooltip(
      builder,
      [tooltip],
      this.xStart + width,
      yOffset - plotOffset / 2,
      skillId ? skillId === this.visibleGroupTooltip : this.props.competencyId === this.visibleGroupTooltip
    );
  }

  renderIndustryBar(
    builder: SVGBuilder,
    width: number,
    yOffset: number,
    plotOffset: number,
    tooltip: string,
    skillId?: string
  ) {
    builder.addLine({
      x1: this.scale(this.xStart),
      y1: this.scale(yOffset),
      x2: this.scale(width + this.xStart),
      y2: this.scale(yOffset),
      style: 'dashed'
    });
    builder.addTriangle({
      x: this.scale(width + this.xStart),
      y: this.scale(yOffset),
      size: this.scale(0.7)
    });
    builder.addRect({
      x: this.scale(this.xStart),
      y: this.scale(yOffset - plotOffset / 2),
      width: this.scale(width + 0.7),
      height: this.scale(plotOffset),
      style: 'transparent',
      onMouseEnter: skillId ? () => this.hoverOnIndustry(skillId) : () => this.hoverOnIndustry(this.props.competencyId),
      onMouseLeave: this.hoverOffIndustry
    });
    this.addTooltip(
      builder,
      [tooltip],
      this.xStart + width,
      yOffset - plotOffset / 2,
      skillId ? skillId === this.visibleIndustryTooltip : this.props.competencyId === this.visibleIndustryTooltip
    );
  }

  scale = (num: number): number => {
    return (num * this.size) / 100;
  };
}

export const CompetencyBars = withI18n(CompetencyBars0);




// WEBPACK FOOTER //
// ./src/ui/components/competency-bars/competency-bars.tsx